import { z } from "zod/v4";
import type { CollectionImageGuardrailFlag } from "../../../../agents/collection-image-guardrails";
import { AgenticSettingsReviewOpType } from "../../types";

// SCHEMA ONLY. packages/app compiles this through the llm/schema barrel, so keep it free of
// database, storage, and other node-only imports.

const flag = (description: string) =>
  z.boolean().optional().describe(description);

// Keyed by the stored flag names so the wire and the jsonb cannot drift.
const collectionImageGuardrailFlagShape = {
  allowImageGeneration: flag(
    "False blocks every AI edit of this collection's assets and their use as generation references; they can still be placed in ads as-is. Overrides every other flag.",
  ),
  allowFaceEdits: flag("False forbids modifying or generating human faces."),
  allowBodyEdits: flag(
    "False forbids modifying or generating human bodies (pose, physique, limbs).",
  ),
  allowSkinEdits: flag(
    "False forbids retouching or altering skin (smoothing, blemish removal, tone or texture).",
  ),
  allowPeopleGeneration: flag(
    "False forbids generating new people; everyone shown must come from the source photos.",
  ),
  allowBackgroundEdits: flag(
    "False forbids modifying, replacing, or regenerating backgrounds.",
  ),
  allowProductSwaps: flag(
    "False forbids swapping or replacing products shown in the assets.",
  ),
  allowTextRemoval: flag("False forbids removing burned-in text."),
  allowOutfill: flag(
    "False forbids extending the asset beyond its original framing.",
  ),
  allowUpscale: flag("False forbids upscaling or AI resolution enhancement."),
} satisfies Record<CollectionImageGuardrailFlag, ReturnType<typeof flag>>;

const newCustomRuleSchema = z.string().trim().min(1).max(500);

const collectionImageGuardrailsOperationShape = <
  Flags extends z.ZodType<
    Partial<Record<CollectionImageGuardrailFlag, boolean>>
  >,
>(
  flags: Flags,
) => ({
  op_type: z.literal(
    AgenticSettingsReviewOpType.UpdateCollectionImageGuardrails,
  ),
  collection_id: z
    .string()
    .min(1)
    .describe("Id of a collection from read_image_guardrails `collections`."),
  flags: flags
    .optional()
    .describe(
      "Only the flags to change, each set to its new value. Omitted flags keep their current value.",
    ),
  add_custom_rules: z
    .array(newCustomRuleSchema)
    .max(20)
    .optional()
    .describe(
      "New custom rules to append, one restriction per rule, written as an instruction to the image agent (e.g. 'Never alter jersey numbers.'). Use a flag instead when one covers the restriction.",
    ),
  // Uncapped: rules written on the guardrails page have no length or count limit, and
  // each removal must match one exactly.
  remove_custom_rules: z
    .array(z.string().trim().min(1))
    .optional()
    .describe(
      "Existing custom rules to delete, each copied verbatim from the collection's `image_guardrails.customRules` in read_image_guardrails.",
    ),
});

// Strict flags, so a misspelled flag is an error the agent sees instead of a silently
// dropped change.
const contextReviewUpdateCollectionImageGuardrailsOperationSchema = z
  .object(
    collectionImageGuardrailsOperationShape(
      z.strictObject(collectionImageGuardrailFlagShape),
    ),
  )
  .refine(
    (op) =>
      Object.keys(op.flags ?? {}).length > 0 ||
      (op.add_custom_rules?.length ?? 0) > 0 ||
      (op.remove_custom_rules?.length ?? 0) > 0,
    {
      message:
        "update_collection_image_guardrails must set at least one flag or add or remove at least one custom rule",
    },
  );

// Stored rows are read back leniently: a flag that shipped and was rolled back is dropped
// instead of failing every read of the review that holds it.
const storedUpdateCollectionImageGuardrailsOperationSchema = z.object(
  collectionImageGuardrailsOperationShape(
    z.object(collectionImageGuardrailFlagShape),
  ),
);

export const COLLECTION_IMAGE_GUARDRAILS_WIRE_OPERATIONS = [
  contextReviewUpdateCollectionImageGuardrailsOperationSchema,
] as const;

export const COLLECTION_IMAGE_GUARDRAILS_STORED_OPERATIONS = [
  storedUpdateCollectionImageGuardrailsOperationSchema,
] as const;
